import {
  IconAdjustmentsAlt,
  IconAutomation,
  IconBuilding,
  IconFileDollar,
  IconMapDollar,
  IconPlugConnected,
  IconSettings,
  IconTemplate,
  IconUserCircle,
  IconUsers,
  IconVariable,
} from '@tabler/icons-react';
import { usePackage } from 'init/use-company-configuration';
import { useProfile } from 'init/use-enforce-profile';
import { tKeys, useTranslation } from 'locales';
import { useMemo } from 'react';
import { INTEGRATIONS_ROUTE } from 'shared/components/Integrations';

import type { Menu, MenuItem } from '../types';

export function useBuildSettingsMenu(): Menu {
  const { t } = useTranslation();
  const profile = useProfile();
  const { automation } = usePackage();

  const canEditWorkflows = profile.roles.includes('admin') || profile.roles.includes('workflow');

  return useMemo(() => {
    const items: Array<Menu | MenuItem> = [
      {
        path: '/settings/configuration',
        label: t(tKeys.PREFERENCES),
        Icon: IconAdjustmentsAlt,
        isHidden: !profile.isAdmin,
      },
      {
        path: '/settings/company',
        label: t(tKeys.SETTINGS.COMPANY.TITLE),
        Icon: IconBuilding,
        isHidden: !profile.isAdmin,
      },
      {
        path: '/settings/billing',
        label: t(tKeys.COMMON.INVOICING),
        Icon: IconFileDollar,
        isHidden: !profile.isAdmin,
      },
      {
        path: '/settings/workflows',
        label: t(tKeys.AUTOMATED_PLANS),
        Icon: IconMapDollar,
        isHidden: !canEditWorkflows,
        isExactRouteMatch: false,
      },
      {
        path: '/settings/templates',
        label: t(tKeys.TEMPLATES),
        Icon: IconTemplate,
        isHidden: !canEditWorkflows,
        isExactRouteMatch: false,
      },
      {
        path: '/settings/variables',
        label: t(tKeys.NAV.CUSTOMIZATION),
        Icon: IconVariable,
        isExactRouteMatch: false,
        isHidden: !profile.isAdmin,
      },
      {
        path: '/settings/automations',
        label: t(tKeys.AUTOMATION.INDEX.TITLE),
        Icon: IconAutomation,
        showUpsell: automation === 'no_automations',
        isHidden: !profile.isAdmin,
      },
      {
        path: INTEGRATIONS_ROUTE,
        label: t(tKeys.SETTINGS.INTEGRATIONS.TITLE),
        Icon: IconPlugConnected,
        isHidden: !profile.isAdmin,
      },
      {
        path: '/settings/collaborators',
        label: t(tKeys.SETTINGS.STAFF.TITLE),
        Icon: IconUsers,
        isHidden: !profile.isAdmin,
      },
      {
        path: '/settings/user',
        label: t(tKeys.SETTINGS.PROFILE.TITLE),
        Icon: IconUserCircle,
      },
    ];

    return {
      parent: null,
      label: t(tKeys.NAV.SETTINGS),
      Icon: IconSettings,
      path: '/settings/configuration',
      items,
    };
  }, [t, canEditWorkflows, automation, profile.isAdmin]);
}
